// =============================================================================
// ACT READING TEST BANK - Form J08
// Source: ACT October 2025 Release
// Total Questions: 36 (27 scored, 9 field test removed in this version)
// =============================================================================

export const formId = 'J08';
export const subject = 'reading';
export const totalQuestions = 27; // Scored questions only (field test removed)
export const scoredQuestions = 27;
export const timeLimit = 35; // minutes (for scored questions only)
export const standardTime = 40; // Full test time

// Field test questions were removed from this release
export const fieldTestPositions = [];

// -----------------------------------------------------------------------------
// SCORING TABLE (27 scored questions → 1-36 scale)
// -----------------------------------------------------------------------------
export const scoringTable = {
  27: 36, 26: 36, 25: 35, 24: 34, 23: 33, 22: 32, 21: 30, 20: 29, 19: 28,
  18: 26, 17: 25, 16: 24, 15: 23, 14: 22, 13: 20, 12: 19, 11: 17, 10: 16,
  9: 14, 8: 13, 7: 12, 6: 11, 5: 10, 4: 9, 3: 7, 2: 5, 1: 3, 0: 1
};

// -----------------------------------------------------------------------------
// CATEGORY DEFINITIONS
// -----------------------------------------------------------------------------
export const categories = {
  KID: { name: 'Key Ideas & Details', description: 'Central ideas, summarizing, inferences, relationships', count: 12 },
  CS: { name: 'Craft & Structure', description: 'Word meaning, author\'s purpose, text structure', count: 9 },
  IKI: { name: 'Integration of Knowledge & Ideas', description: 'Claims, evidence, multiple texts', count: 6 }
};

// -----------------------------------------------------------------------------
// PASSAGES
// -----------------------------------------------------------------------------
export const passages = [
  {
    id: 'J08-R1',
    title: 'Producing and Collecting Portraits of Artists',
    author: 'Anna Reynolds and Lucy Peter',
    type: 'informational',
    genre: 'Art History',
    source: 'Published by Royal Collection Trust / © Her Majesty Queen Elizabeth II 2016',
    questionRange: [1, 9],
    description: 'An essay exploring the relationship between mirrors and the development of self-portraiture, from convex mirrors to flat glass mirrors.',
    passageText: `One practical consideration with painted self-portraits, which is easy to forget today, was the availability of mirrors. Flat glass mirrors were invented in Venice in around 1500. Before this date, mirrors were small and convex, and therefore a potential deterrent to artists. However, attributing the development of self-portraiture purely to the development of mirrors is an oversimplification. While flat mirrors were available from c.1500, they were also prohibitively expensive: it was not until the 1700s, when the French started producing mirrors of the same size and quality as the Venetians, that the market opened up and mirrors became more affordable...`
  },
  {
    id: 'J08-R2',
    title: 'Paired Literary Narratives',
    subtitle: 'Passage A: "All the Flowers in the Air" / Passage B: "Central Places"',
    author: 'Passage A: Aarti Monteiro / Passage B: Delia Cai',
    type: 'paired-literary',
    genre: 'Short Story / Novel Excerpt',
    source: 'Passage A: ©2021 by Kweli Journal, Inc. / Passage B: ©2023 by Delia Cai',
    questionRange: [10, 18],
    description: 'Two narratives exploring themes of home, belonging, and returning to places from one\'s past. Passage A follows Rani visiting her old neighborhood in Bombay; Passage B follows a narrator returning home with her fiancé Ben.',
    passageAText: `With one more day in Bombay, Rani decided to visit her old neighborhood. She followed the route to the train station Siya had laid out and bought a ticket...`,
    passageBText: `Inside the H Mart, the instant confrontation with ninety thousand square feet of food laid out under a battery of flashing screens and fluorescent bulbs and plastic holly makes me want to stop and massage my temples a little...`
  },
  {
    id: 'J08-R3',
    title: 'Life Springs',
    author: 'Martin J. Van Kranendonk, David Deamer, and Tara Djokic',
    type: 'informational',
    genre: 'Science',
    source: '©2017 by Scientific American',
    questionRange: [19, 27],
    description: 'Scientists discuss discoveries at the Dresser Formation in Australia that support a new theory about where life on Earth began—in volcanic hot springs on land rather than in deep ocean hydrothermal vents.',
    passageText: `Heading up the side of the creek embankment, Djokic suddenly stumbles back downhill. Has she lost her balance? To stop her from falling, Van Kranendonk reaches out to stop her and pushes her back uphill...`
  }
];

// -----------------------------------------------------------------------------
// QUESTIONS
// -----------------------------------------------------------------------------
export const questions = [
  // PASSAGE I: Producing and Collecting Portraits of Artists (Q1-9)
  {
    id: 'J08-R01',
    formId: 'J08',
    questionNumber: 1,
    passageId: 'J08-R1',
    category: 'CS',
    isScored: true,
    question: 'The main purpose of the passage is to:',
    choices: [
      { letter: 'A', text: 'provide an overview of famous self-portraits created using flat glass mirrors.' },
      { letter: 'B', text: 'explore trends in the art world that led to the development of self-portraiture.' },
      { letter: 'C', text: 'describe the relationship and significance of mirrors to the development of self-portraits.' },
      { letter: 'D', text: 'explain the history of and manufacturing methods involved in the development of mirrors.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The passage primarily discusses how the development and availability of mirrors (both convex and flat) influenced the development of self-portraiture throughout history.'
  },
  {
    id: 'J08-R02',
    formId: 'J08',
    questionNumber: 2,
    passageId: 'J08-R1',
    category: 'KID',
    isScored: true,
    question: 'The main point of the first paragraph (lines 1–20) is that:',
    choices: [
      { letter: 'F', text: 'while mirrors were not wholly responsible for the development of self-portraiture, they likely played an important role.' },
      { letter: 'G', text: 'mirrors enabled people to see themselves, which likely had a great social impact.' },
      { letter: 'H', text: 'flat glass mirrors were invented in Venice around the year 1500.' },
      { letter: 'J', text: 'before flat mirrors were invented, convex mirrors were the norm and not particularly helpful to most self-portraitists.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'The first paragraph explicitly states that attributing self-portraiture purely to mirrors is an oversimplification, but acknowledges that mirrors must have had "a considerable social impact" on self-portraiture.'
  },
  {
    id: 'J08-R03',
    formId: 'J08',
    questionNumber: 3,
    passageId: 'J08-R1',
    category: 'KID',
    isScored: true,
    question: 'Based on the passage, one similarity shared by A Man in Armour and Lady at the Virginals with a Gentleman is:',
    choices: [
      { letter: 'A', text: 'the featuring of a convex mirror in the paintings.' },
      { letter: 'B', text: 'the inclusion of flat glass mirrors in the paintings.' },
      { letter: 'C', text: 'a focus on domestic interiors.' },
      { letter: 'D', text: 'the biographical backgrounds of the artists.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'The passage states both paintings feature flat glass mirrors—A Man in Armour shows the sitter "reflected in two flat glass mirrors" and Vermeer\'s Lady at the Virginals shows "a half-length mirror on the wall."'
  },
  {
    id: 'J08-R04',
    formId: 'J08',
    questionNumber: 4,
    passageId: 'J08-R1',
    category: 'CS',
    isScored: true,
    question: 'In the context of the passage, the author uses the phrase "fuel the vanity" (line 19) mainly to:',
    choices: [
      { letter: 'F', text: 'question Renaissance artists\' commitment to the genre of the self-portrait.' },
      { letter: 'G', text: 'suggest one reason why the availability of mirrors might have resulted in more self-portraits.' },
      { letter: 'H', text: 'dispute the idea that mirrors were a significant cause of vanity during the Renaissance.' },
      { letter: 'J', text: 'explain why the availability of mirrors affected some artists more than others.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'The phrase is used to suggest that being able to see oneself more clearly encouraged "greater physical self-awareness" and helped motivate artists to create self-portraits.'
  },
  {
    id: 'J08-R05',
    formId: 'J08',
    questionNumber: 5,
    passageId: 'J08-R1',
    category: 'KID',
    isScored: true,
    question: 'Which of the following statements most effectively captures the process by which convex mirrors were produced?',
    choices: [
      { letter: 'A', text: 'A ball of molten metal was formed, covered with glass, allowed to cool, and then cut into individual pieces.' },
      { letter: 'B', text: 'A ball of molten metal was formed, allowed to cool, cut into individual pieces, and then covered with glass.' },
      { letter: 'C', text: 'A ball of blown glass was formed, cut into individual pieces, filled with molten metal, and then allowed to cool.' },
      { letter: 'D', text: 'A ball of blown glass was formed, filled with molten metal, allowed to cool, and then cut into individual pieces.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'The passage states convex mirrors "were created from balls of blown glass filled with molten metal... and once cool, sections of the ball were cut to form individual pieces."'
  },
  {
    id: 'J08-R06',
    formId: 'J08',
    questionNumber: 6,
    passageId: 'J08-R1',
    category: 'KID',
    isScored: true,
    question: 'Based on the passage, which of the following statements best summarizes the role of convex mirrors after flat mirrors came into use for artists?',
    choices: [
      { letter: 'F', text: 'Although flat mirrors had obvious advantages for self-portraitists, many artists refused to use them, opting instead to use the older, more traditional convex mirrors.' },
      { letter: 'G', text: 'Although the convex mirror became a less popular tool for artists after flat mirrors became available, it nonetheless remained a well-known symbol of the artist.' },
      { letter: 'H', text: 'Some artists who began using flat mirrors chose to return to using convex mirrors for particular types of works, especially narrative paintings.' },
      { letter: 'J', text: 'Once convex mirrors were used less often than flat mirrors for self-portraits, the convex mirror became a symbol of outdated, unfashionable artistic practices.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'The passage states that even after flat mirrors were invented, "many artists continued to allude to convex mirrors in their work; indeed, it was to become an attribute of the artist."'
  },
  {
    id: 'J08-R07',
    formId: 'J08',
    questionNumber: 7,
    passageId: 'J08-R1',
    category: 'KID',
    isScored: true,
    question: 'According to the passage, one development that allowed for the invention of flat mirrors was a new kind of glass that was particularly:',
    choices: [
      { letter: 'A', text: 'hard.' },
      { letter: 'B', text: 'sturdy.' },
      { letter: 'C', text: 'lightweight.' },
      { letter: 'D', text: 'clear.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'The passage states flat glass mirrors were made possible by "the discovery of a new type of glass known as \'cristallo\', so clear it was compared to rock crystal."'
  },
  {
    id: 'J08-R08',
    formId: 'J08',
    questionNumber: 8,
    passageId: 'J08-R1',
    category: 'KID',
    isScored: true,
    question: 'According to the passage, mirrors became widely available in the nineteenth century primarily because of:',
    choices: [
      { letter: 'F', text: 'the discovery of a new type of glass.' },
      { letter: 'G', text: 'the development of mercury and tin as silvering agents.' },
      { letter: 'H', text: 'a more economical silvering technique.' },
      { letter: 'J', text: 'consumers finally expressing a need for mirrors.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'The passage states "It was not until the nineteenth century, with the discovery of a cheaper technique for silvering glass, that mirrors of a standard thickness and quality were finally available to the mass market."'
  },
  {
    id: 'J08-R09',
    formId: 'J08',
    questionNumber: 9,
    passageId: 'J08-R1',
    category: 'CS',
    isScored: true,
    question: 'As it is used in line 87, the word fixing most nearly means:',
    choices: [
      { letter: 'A', text: 'focusing on.' },
      { letter: 'B', text: 'adhering to.' },
      { letter: 'C', text: 'stabilizing.' },
      { letter: 'D', text: 'correcting.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'In the context of the artist "fixing the viewer with a steady outward gaze," the word means directing attention toward or focusing on the viewer.'
  },

  // PASSAGE II: Paired Literary Narratives (Q10-18)
  {
    id: 'J08-R10',
    formId: 'J08',
    questionNumber: 10,
    passageId: 'J08-R2',
    category: 'CS',
    isScored: true,
    question: 'It can reasonably be inferred that the author of Passage A uses the metaphors "current of people" (line 20) and "each curve a current" (line 27) primarily to:',
    choices: [
      { letter: 'F', text: 'suggest that Rani is to some degree being compelled onward by external forces.' },
      { letter: 'G', text: 'emphasize the number of people in the crowds traveling alongside Rani.' },
      { letter: 'H', text: 'indicate that Rani is trying to remind herself to stay present and aware in the moment.' },
      { letter: 'J', text: 'underscore that the thought of returning home is deeply exciting to Rani.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'The "current" metaphors suggest Rani is being moved along by forces outside her control, like water carrying her toward her destination.'
  },
  {
    id: 'J08-R11',
    formId: 'J08',
    questionNumber: 11,
    passageId: 'J08-R2',
    category: 'CS',
    isScored: true,
    question: 'As it is used in Passage A, the phrase "laid out" (line 3) most nearly means:',
    choices: [
      { letter: 'A', text: 'outlined.' },
      { letter: 'B', text: 'invented.' },
      { letter: 'C', text: 'projected.' },
      { letter: 'D', text: 'interpreted.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Siya "laid out" the route, meaning she provided directions or outlined the path Rani should take.'
  },
  {
    id: 'J08-R12',
    formId: 'J08',
    questionNumber: 12,
    passageId: 'J08-R2',
    category: 'KID',
    isScored: true,
    question: 'In Passage B, compared to the narrator, the narrator\'s father is characterized as being more focused on:',
    choices: [
      { letter: 'F', text: 'chatting about the narrator\'s family.' },
      { letter: 'G', text: 'selecting various grocery items to buy.' },
      { letter: 'H', text: 'observing Ben\'s interactions with store patrons.' },
      { letter: 'J', text: 'helping Ben learn how to pick out the best produce.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'The passage describes the father "gradually loses interest in watching Ben and then revs into grocery mode, picking out crates of Asian pears and fat persimmons."'
  },
  {
    id: 'J08-R13',
    formId: 'J08',
    questionNumber: 13,
    passageId: 'J08-R2',
    category: 'KID',
    isScored: true,
    question: 'In Passage B, the narrator\'s thoughts in response to her father\'s remark about Ben\'s photography most nearly suggest that the narrator is:',
    choices: [
      { letter: 'A', text: 'skeptical that her father has any true understanding of photojournalism.' },
      { letter: 'B', text: 'overjoyed that her father already seems to value Ben\'s professional skills.' },
      { letter: 'C', text: 'proud of being the one to introduce her father to the world of photojournalism.' },
      { letter: 'D', text: 'bewildered that her father doesn\'t ask Ben any questions about photography.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The narrator notes her father says "He\'s very good" but thinks "I am sure that Ben is the first photographer, and journalist, and photojournalist, that my dad has ever met in his entire life," suggesting skepticism.'
  },
  {
    id: 'J08-R14',
    formId: 'J08',
    questionNumber: 14,
    passageId: 'J08-R2',
    category: 'CS',
    isScored: true,
    question: 'As it is used in Passage B, the phrase "chronically starry-eyed" (lines 81–82) most nearly means that Ben:',
    choices: [
      { letter: 'F', text: 'can often dazzle people with his charm.' },
      { letter: 'G', text: 'is regularly found daydreaming instead of working.' },
      { letter: 'H', text: 'has a consistently optimistic outlook on life.' },
      { letter: 'J', text: 'is frequently perceived by others as aloof and shy.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '"Starry-eyed" means idealistic or optimistic, and "chronically" means consistently or persistently. The context of Ben being "Mr. Up for Whatever" reinforces this interpretation.'
  },
  {
    id: 'J08-R15',
    formId: 'J08',
    questionNumber: 15,
    passageId: 'J08-R2',
    category: 'IKI',
    isScored: true,
    question: 'The passages are structurally similar in that both mainly:',
    choices: [
      { letter: 'A', text: 'relate events that take place within a single day.' },
      { letter: 'B', text: 'recount characters\' feelings and thoughts about moments from their past.' },
      { letter: 'C', text: 'describe a particular event and its long-term effects.' },
      { letter: 'D', text: 'alternate between relating characters\' memories of the past and events occurring in the present.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Both passages describe events taking place during a single visit—Rani\'s day trip to her old neighborhood, and the narrator\'s arrival at the H Mart with her fiancé and father.'
  },
  {
    id: 'J08-R16',
    formId: 'J08',
    questionNumber: 16,
    passageId: 'J08-R2',
    category: 'IKI',
    isScored: true,
    question: 'Which of the following statements best captures a difference in the way the passages explore the themes of home and belonging?',
    choices: [
      { letter: 'F', text: 'Passage A focuses on how Rani regrets leaving home; Passage B discusses the narrator\'s newfound desire to return home.' },
      { letter: 'G', text: 'Passage A considers how the place that was once home for Rani has changed; Passage B discusses how the narrator realizes that she herself is different.' },
      { letter: 'H', text: 'Passage A examines the lasting impact on Rani of leaving home as a child; Passage B considers the impact that returning home has on the narrator\'s relationship with her parents.' },
      { letter: 'J', text: 'Passage A focuses on how Rani feels that the people she once knew are different; Passage B examines how the narrator\'s hometown has changed.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Passage A describes how Rani\'s childhood home has been replaced by a high-rise. Passage B ends with the narrator realizing she is "coming back to this place as a different person than the one who left."'
  },
  {
    id: 'J08-R17',
    formId: 'J08',
    questionNumber: 17,
    passageId: 'J08-R2',
    category: 'IKI',
    isScored: true,
    question: 'It can most reasonably be inferred from the passages that compared to the narrator of Passage B, Rani in Passage A would be more likely to describe her experience of visiting her former home as:',
    choices: [
      { letter: 'A', text: 'amusing.' },
      { letter: 'B', text: 'pleasant.' },
      { letter: 'C', text: 'stimulating.' },
      { letter: 'D', text: 'disappointing.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Passage A explicitly states "The disappointment of returning home is the burden of anyone who leaves," and describes Rani feeling "something tangle inside her like weeds" when she sees her old home replaced.'
  },
  {
    id: 'J08-R18',
    formId: 'J08',
    questionNumber: 18,
    passageId: 'J08-R2',
    category: 'IKI',
    isScored: true,
    question: 'Which of the following statements best describes how the tone of the last paragraph of Passage A (lines 42–46) compares to the tone of the last paragraph of Passage B (lines 76–89)?',
    choices: [
      { letter: 'F', text: 'The tones are similar in that both reflect the contentment the characters feel with their circumstances.' },
      { letter: 'G', text: 'The tones are similar in that both reflect the hesitancy and indecision the characters feel about what to do next.' },
      { letter: 'H', text: 'The tones are different in that Passage A ends with a tone of wistfulness, whereas Passage B ends on a hopeful note.' },
      { letter: 'J', text: 'The tones are different in that Passage A ends with a tone of acceptance, whereas Passage B ends on a defiant note.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Passage A ends with melancholy about "the disappointment of returning home." Passage B ends optimistically with the narrator feeling that bringing Ben home "was a good idea" and realizing she\'s become "Mrs. Up for Whatever."'
  },

  // PASSAGE III: Life Springs (Q19-27)
  {
    id: 'J08-R19',
    formId: 'J08',
    questionNumber: 19,
    passageId: 'J08-R3',
    category: 'CS',
    isScored: true,
    question: 'The primary purpose of the passage is to:',
    choices: [
      { letter: 'F', text: 'relate the story of how researchers studying the Dresser Formation found indications of biofilms in geyserite.' },
      { letter: 'G', text: 'explain how evidence from the Dresser Formation supports a new idea about where life on Earth began.' },
      { letter: 'H', text: 'discuss the strengths and weaknesses of theories about where life on Earth began.' },
      { letter: 'J', text: 'provide a chronology of the prevailing beliefs about when and where life on Earth began.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'The passage primarily describes how discoveries at the Dresser Formation support the theory that life began in land-based volcanic pools rather than deep ocean vents.'
  },
  {
    id: 'J08-R20',
    formId: 'J08',
    questionNumber: 20,
    passageId: 'J08-R3',
    category: 'KID',
    isScored: true,
    question: 'Which of the following events mentioned in the passage occurred last chronologically?',
    choices: [
      { letter: 'A', text: 'Van Kranendonk reached out to push Djokic back uphill.' },
      { letter: 'B', text: 'Djokic discovered indications of biofilms in the Dresser Formation.' },
      { letter: 'C', text: 'Djokic encountered the spiderweb.' },
      { letter: 'D', text: 'The researchers began hiking through the Pilbara at night.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The passage opens with the spider encounter during the night hike back from their day\'s work. The sequence is: discover biofilms during the day → begin hiking at night → encounter spiderweb → Van Kranendonk pushes Djokic.'
  },
  {
    id: 'J08-R21',
    formId: 'J08',
    questionNumber: 21,
    passageId: 'J08-R3',
    category: 'KID',
    isScored: true,
    question: 'Based on lines 13–26 and 71–74, what can reasonably be inferred about the relationship between Djokic\'s and Deamer\'s discoveries?',
    choices: [
      { letter: 'F', text: 'Djokic found geologic evidence to support what Deamer had shown was possible.' },
      { letter: 'G', text: 'Deamer found the same geologic evidence in the Dresser a decade earlier than Djokic did.' },
      { letter: 'H', text: 'Deamer\'s research later proved that Djokic had found evidence of early life on Earth.' },
      { letter: 'J', text: 'Djokic\'s discovery undermined Deamer\'s research from ten years earlier.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Deamer had previously shown that volcanic pools "could foster the assembly of compartments made of membranes." Djokic later found geologic evidence that such volcanic pools existed in the Dresser Formation.'
  },
  {
    id: 'J08-R22',
    formId: 'J08',
    questionNumber: 22,
    passageId: 'J08-R3',
    category: 'IKI',
    isScored: true,
    question: 'Which of the following details from the passage most directly challenges the claim that life originated in the ocean?',
    choices: [
      { letter: 'A', text: 'Primitive bacteria weren\'t found on ocean-based hydrothermal vents until 1977.' },
      { letter: 'B', text: 'In vast amounts of water, molecules may spread out too quickly to interact.' },
      { letter: 'C', text: 'Cataclysms wracked the Earth about four billion years ago.' },
      { letter: 'D', text: 'Hydrothermal vents emit gases such as hydrogen sulfide.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'The passage states "The big one [problem with the ocean theory] is that the ocean has a lot of water, and in it the needed molecules might spread out too quickly to interact and form cell membranes."'
  },
  {
    id: 'J08-R23',
    formId: 'J08',
    questionNumber: 23,
    passageId: 'J08-R3',
    category: 'KID',
    isScored: true,
    question: 'Which of the following statements best summarizes the passage\'s description of polymers in land pools?',
    choices: [
      { letter: 'F', text: 'Polymers form during a heated dry spell, and then they float around and interact with each other during wet spells.' },
      { letter: 'G', text: 'Polymers form and interact in the pools during wet spells, then float around and become fatty acids during dry spells.' },
      { letter: 'H', text: 'Polymers form during a dry spell, float around during a wet spell, and interact with each other when the pools dry out again.' },
      { letter: 'J', text: 'Polymers repeatedly dry out and get wet again, which allows them to interact with each other.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'The passage describes "dry spells in which complex molecules called polymers can be formed from simpler units, wet spells that float these polymers around, and further drying periods that maroon them in tiny cavities where they can interact."'
  },
  {
    id: 'J08-R24',
    formId: 'J08',
    questionNumber: 24,
    passageId: 'J08-R3',
    category: 'CS',
    isScored: true,
    question: 'As it is used in line 11, the word odd most nearly means:',
    choices: [
      { letter: 'A', text: 'unorthodox.' },
      { letter: 'B', text: 'eccentric.' },
      { letter: 'C', text: 'miscellaneous.' },
      { letter: 'D', text: 'unlikely.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'In context, "the odd benign species" means encountering a harmless spider would be unlikely given that Australian spiders "have a deservedly bad reputation."'
  },
  {
    id: 'J08-R25',
    formId: 'J08',
    questionNumber: 25,
    passageId: 'J08-R3',
    category: 'IKI',
    isScored: true,
    question: 'In the passage, what evidence mentioned by the authors best supports the claim "it was a thriving ecosystem" (lines 33–34)?',
    choices: [
      { letter: 'F', text: 'Biologists\' theories about hydrothermal vents after the 1977 Alvin discovery' },
      { letter: 'G', text: 'The minerals, gases, and organisms that the Alvin detected around hydrothermal vents' },
      { letter: 'H', text: 'The similarities between the ecosystems of hydrothermal vents and volcanic pools' },
      { letter: 'J', text: 'How primitive bacteria use iron and sulfur to form cell membranes' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'The passage describes Alvin discovering "hydrothermal vents... pumping out minerals containing iron and sulfur and gases such as methane and hydrogen sulfide, surrounded by primitive bacteria and large worms."'
  },
  {
    id: 'J08-R26',
    formId: 'J08',
    questionNumber: 26,
    passageId: 'J08-R3',
    category: 'CS',
    isScored: true,
    question: 'The passage authors include the details about Yellowstone National Park primarily to:',
    choices: [
      { letter: 'A', text: 'provide an example of where fossilized signs of life have been found.' },
      { letter: 'B', text: 'acknowledge where life on Earth likely began.' },
      { letter: 'C', text: 'portray what the Dresser once looked like.' },
      { letter: 'D', text: 'emphasize how similar the Dresser\'s current climate is to Yellowstone\'s.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The passage states "the Dresser, now a dry, hot and barren outback environment, had once been like the steaming pools and erupting geysers of Yellowstone National Park."'
  },
  {
    id: 'J08-R27',
    formId: 'J08',
    questionNumber: 27,
    passageId: 'J08-R3',
    category: 'KID',
    isScored: true,
    question: 'According to the passage, establishing a land-based theory for life\'s origins will likely influence future research pertaining to:',
    choices: [
      { letter: 'F', text: 'prototypes of cell membranes.' },
      { letter: 'G', text: 'sedimentary rocks in the Pilbara.' },
      { letter: 'H', text: 'how hydrothermal vents function.' },
      { letter: 'J', text: 'the search for alien life.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'The passage states "in our search for alien life elsewhere in the solar system, a land-based theory about origins would guide us to different places and planets than would an ocean-based theory."'
  }
];

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------
export function getQuestion(questionNumber) {
  return questions.find(q => q.questionNumber === questionNumber);
}

export function getPassageQuestions(passageId) {
  return questions.filter(q => q.passageId === passageId);
}

export function getScoredQuestions() {
  return questions.filter(q => q.isScored);
}

export function getQuestionsByCategory(category) {
  return questions.filter(q => q.category === category);
}

export function calculateScaledScore(rawScore) {
  if (rawScore < 0) return 1;
  if (rawScore > 27) return 36;
  return scoringTable[rawScore] || 1;
}

// Default export
export default {
  formId,
  subject,
  totalQuestions,
  scoredQuestions,
  timeLimit,
  scoringTable,
  categories,
  passages,
  questions,
  getQuestion,
  getPassageQuestions,
  getScoredQuestions,
  getQuestionsByCategory,
  calculateScaledScore
};
